import {Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {distinctUntilChanged, finalize, Subject, take, takeUntil} from 'rxjs';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {FuseNavigationService, FuseVerticalNavigationComponent} from '@fuse/components/navigation';
import {Navigation} from 'app/core/navigation/navigation.types';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {User} from 'app/core/user/user.types';
import {versionInfo} from '../../../../../../version-info';
import {LocalStorageService} from '../../../../shared/services/local-storage.service';
import {get} from 'lodash';
import {SnackbarTypes} from '../../../../../@fuse/services/confirmation/snackbar/snackbar.component';
import {PathEnum} from '../../../../app.routing';
import {FuseConfirmationService} from '../../../../../@fuse/services/confirmation';
import {TranslocoService} from '@jsverse/transloco';
import {AutenticazioneService, UtentiService} from '../../../../../api-clients/generated/services';
import {ComuneService} from "../../../../shared/services/comune.service";

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {

    @ViewChild(FuseVerticalNavigationComponent) fuseVerticalNavigationComponent: FuseVerticalNavigationComponent
    isScreenSmall: boolean;
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    tag: string | undefined;
    private isAdmin: boolean;
    @Input() noSidebar;
    @Input() showSidebar;
    isOpComune: boolean;
    isOpCottura: boolean;
    isOpScuola: boolean;
    showReportPage = false;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        public _fuseConfirmationService: FuseConfirmationService,
        private _translocoService: TranslocoService,
        //private _authService: AuthService,
        private localStorageService: LocalStorageService,
        private userService: UtentiService,
        private authService: AutenticazioneService,
        private comuneService: ComuneService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.isAdmin = this.localStorageService.isAdmin();
        this.isOpComune = this.localStorageService.isOpComune();
        this.isOpCottura = this.localStorageService.isOpCottura();
        this.isOpScuola = this.localStorageService.isOpScuola();
        this.showReportPage = (this.localStorageService.isOpScuola() && this.comuneService.listaComuni.length > 0
                && this.comuneService.listaComuni.some(c => c.gestioneManuale))
            || (this.isAdmin || this.isOpComune || this.isOpCottura)

        this.comuneService.getSlugComune$
            .pipe(distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.updateFlagsOnComuneChange();
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                console.log('navigation', this.navigation)
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this.tag = versionInfo?.tag?.split('-')[0];

        if (this.localStorageService.getUser()) {
            this.user = JSON.parse(this.localStorageService.getUser());
        }

    }

    private updateFlagsOnComuneChange(): void {

        this.isAdmin = this.localStorageService.isAdmin();
        this.isOpComune = this.localStorageService.isOpComune();
        this.isOpCottura = this.localStorageService.isOpCottura();
        this.isOpScuola = this.localStorageService.isOpScuola();


        this.showReportPage =
            (this.localStorageService.isOpScuola() &&
                this.comuneService.listaComuni.length > 0 &&
                this.comuneService.listaComuni.some(c => c.gestioneManuale)) ||
            (this.isAdmin || this.isOpComune || this.isOpCottura);

        this.updateMenuLabels();
        this.fuseVerticalNavigationComponent?.refresh();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    signOut(): void {
        this._fuseConfirmationService.showLoader();
        this.authService.logoutUser().pipe(
            finalize(() => {
                const activeLang = this._translocoService.getActiveLang();
                const translation = this._translocoService.getTranslation().get(activeLang);
                this.localStorageService.cleanToken();
                this._fuseConfirmationService.openSnackBar({
                    message: get(translation, 'home.text_success_sign_out', null),
                    type: SnackbarTypes.Success,
                });
                this._fuseConfirmationService.hideLoader();
                this._router.navigate(['/' + PathEnum.SIGN_IN]);
            }),
            take(1)
        ).subscribe();
    }

    private updateMenuLabels(): void {
        const hasMisto = this.comuneService.hasMisto;
        const soloStrutture = this.comuneService.hasStrutture && !this.comuneService.hasScuole;

        const groupTitle = hasMisto ? 'common.scuole_strutture' : soloStrutture ? 'common.strutture' : 'common.scuole';
        const listaTitle = hasMisto ? 'common.lista_scuole_strutture' : soloStrutture ? 'common.lista_strutture' : 'common.lista_scuole';

        const scuoleGroup = this.navigation.default.find(
            item => item.link === '/' + PathEnum.SCUOLE || item.children?.some(c => c.link === '/' + PathEnum.SCUOLE)
        );
        if (scuoleGroup) {
            scuoleGroup.title = groupTitle;
            const listaItem = scuoleGroup.children?.find(c => c.link === '/' + PathEnum.SCUOLE);
            if (listaItem) {
                listaItem.title = listaTitle;
            }
        }
        const utentiGroup = this.navigation.default.find(item => item.title === 'common.utenti');
        const opScuolaItem = utentiGroup?.children?.find(c => c.link === '/' + PathEnum.SCUOLA);
        if (opScuolaItem) {
            opScuolaItem.title = this.comuneService.hasStrutture ? 'common.op-sede' : 'menu.scuola';
        }
    }

    navigation: Navigation = {
        default: [
            {
                title: 'common.home',
                type: 'basic',
                link: '/' + PathEnum.HOME,
                icon: 'home'
            },
            {
                title: 'menu.funzioni_comune',
                type: 'group',
                hidden: item => !(this.isAdmin || this.isOpComune),
                children: [
                    {
                        title: 'menu.tipi_pratiche',
                        type: 'basic',
                        link: '/' + PathEnum.PRATICHE,
                        icon: 'assignment',
                        hidden: item => !(this.isAdmin || this.isOpComune),
                    },
                    {
                        title: 'menu.configurazione_servizio',
                        type: 'basic',
                        link: '/' + PathEnum.RIEPILOGO_COMUNE,
                        icon: 'tune',
                        hidden: item => !(this.isAdmin || this.isOpComune),
                    },
                ]
            },
            {
                title: 'menu.centro-cottura',
                type: 'group',
                hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune || this.isOpCottura),
                children: [
                    {
                        title: 'menu.menu',
                        hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune || this.isOpCottura),
                        type: 'basic',
                        link: '/' + PathEnum.MENU,
                        icon: 'restaurant_menu'
                    },
                    {
                        title: 'menu.pasti_da_preparare',
                        hidden: item => !(this.isAdmin || this.isOpComune || this.isOpCottura),
                        type: 'basic',
                        link: '/' + PathEnum.PASTI_DA_PREPARARE,
                        icon: 'microwave'
                    },
                    {
                        title: 'menu.pasti_da_preparare_globale',
                        hidden: item => !(this.isAdmin || this.isOpComune || this.isOpCottura),
                        type: 'basic',
                        link: '/' + PathEnum.PASTI_DA_PREPARARE_GLOBALE,
                        icon: 'microwave'
                    },
                    {
                        title: 'menu.pasti_da_preparare_report',
                        hidden: item => !this.showReportPage,
                        type: 'basic',
                        link: '/' + PathEnum.PASTI_DA_PREPARARE_REPORT,
                        icon: 'microwave'
                    },
                ]
            },
            {
                title: this.comuneService.hasMisto ? 'common.scuole_strutture' : this.comuneService.hasStrutture ? 'common.strutture' : 'common.scuole',
                type: 'group',
                hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune),
                children: [
                    {
                        title: this.comuneService.hasMisto ? 'common.lista_scuole_strutture' : this.comuneService.hasStrutture ? 'common.lista_strutture' : 'common.lista_scuole',
                        hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune),
                        type: 'basic',
                        link: '/' + PathEnum.SCUOLE,
                        icon: 'location_city'
                    },
                ]
            },
            {
                title: 'Studenti',
                type: 'group',
                hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune),
                children: [
                    {
                        title: 'menu.lista_studenti',
                        type: 'basic',
                        link: '/' + PathEnum.STUDENTI,
                        icon: 'school',
                        hidden: item => !(this.isAdmin || this.isOpScuola || this.isOpComune),
                    },
                    {
                        title: 'menu.gestione_saldi_studenti',
                        type: 'basic',
                        link: '/' + PathEnum.SALDI_STUDENTI,
                        icon: 'account_balance',
                        hidden: item => !(this.isAdmin || this.isOpComune),
                    },
                    {
                        title: 'menu.pagamenti',
                        type: 'basic',
                        link: '/' + PathEnum.PAGAMENTI,
                        icon: 'payments',
                        hidden: item => !(this.isAdmin || this.isOpComune),
                    },
                    {
                        title: 'menu.rendicontazioni',
                        type: 'basic',
                        link: '/' + PathEnum.RENDICONTAZIONI,
                        icon: 'receipt_long',
                        hidden: item =>
                            !(
                                (this.isAdmin || this.isOpComune) &&
                                this.comuneService.isPagoPaRendicontazione
                            ),
                    }
                ]
            },

            {
                title: 'common.utenti',
                type: 'group',
                hidden: item => !this.isAdmin,
                children: [
                    {
                        title: 'menu.amministratori',
                        type: 'basic',
                        link: '/' + PathEnum.AMMINISTRATORI,
                        icon: 'groups',
                        hidden: item => !this.isAdmin,
                    },
                    {
                        title: 'menu.comune',
                        type: 'basic',
                        link: '/' + PathEnum.COMUNE,
                        icon: 'admin_panel_settings',
                        hidden: item => !this.isAdmin,
                    },
                    {
                        title: 'menu.centro-cottura',
                        type: 'basic',
                        link: '/' + PathEnum.CENTRO_COTTURA,
                        icon: 'countertops',
                        hidden: item => !this.isAdmin,
                    },
                    {
                        title: 'Genitori',
                        type: 'collapsable',
                        icon: 'group',
                        hidden: item => !this.isAdmin && !this.isOpComune,
                        children: [
                            {
                                title: 'menu.tutti',
                                type: 'basic',
                                link: '/' + PathEnum.GENITORI,
                                hidden: item => !this.isAdmin && !this.isOpComune,
                                icon: 'group',
                            },
                            {
                                title: 'menu.bloccati',
                                type: 'basic',
                                link: '/' + PathEnum.GENITORI_BLOCCATI,
                                hidden: item => !this.isAdmin,
                                icon: 'group',
                            },
                            {
                                title: 'menu.mail_non_verificata',
                                type: 'basic',
                                link: '/' + PathEnum.GENITORI_MAIL_NON_VERIFICATA,
                                hidden: item => !this.isAdmin,
                                icon: 'group',
                            },
                            {
                                title: 'menu.sicurezza',
                                type: 'basic',
                                link: '/' + PathEnum.GENITORI_BLOCCATI_SICUREZZA,
                                hidden: item => !this.isAdmin,
                                icon: 'group',
                            },
                        ]
                    },
                    {
                        title: this.comuneService.hasStrutture ? 'common.op-sede' : 'menu.scuola',
                        type: 'basic',
                        link: '/' + PathEnum.SCUOLA,
                        icon: 'apartment',
                        hidden: item => !this.isAdmin,
                    },

                ]
            },
        ]
    }

}
